/**
 * 製作依頼の「試行（1回の製作）」— 2026-11-05。
 *
 * 状態は選ばない。できることは1つだけ（未着手→製作開始 / 製作中→結果を記録 /
 * 一部完成→続きを記録 / 完成→納品する）。結果を記録すると、実際に使った材料が
 * 出庫され、OK数だけ納入先に入庫される。状態は OK累計から決まる。
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { dmsApi } from '@/pages/docs/dmsApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FAB = `${API}/engineering/fabrication`;

export const NG_REASONS = [
  { value: 'warp', label: '反り' },
  { value: 'peel', label: '剥がれ' },
  { value: 'dimension', label: '寸法不良' },
  { value: 'print_fail', label: '造形失敗' },
  { value: 'other', label: 'その他' },
];

const yen = (n) => `¥${Math.round(Number(n || 0)).toLocaleString()}`;

/** 結果を記録（試行1回） */
export const FabAttemptDialog = ({ fab, open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ ok_qty: '', ng_qty: '0', ng_reason: '', ng_note: '',
    material_qty: '', machine_id: '', note: '' });
  const [machines, setMachines] = useState([]);
  const [file, setFile] = useState(null);
  const [docId, setDocId] = useState('');
  const [busy, setBusy] = useState(false);

  const perUnit = Number((fab?.material_lines || [])[0]?.qty_per_unit || 0);
  const matUom = (fab?.material_lines || [])[0]?.uom || 'g';
  const ok = Number(f.ok_qty || 0);
  const ng = Number(f.ng_qty || 0);
  const needPhoto = ng > 0 || (ok > 0 && !(fab?.ok_total > 0));

  // 機械は設備台帳（技術部署・kind=machine）から出す。3Dプリントのときはプリンターを先に。
  useEffect(() => {
    if (!open) return;
    setF({ ok_qty: '', ng_qty: '0', ng_reason: '', ng_note: '', material_qty: '',
      machine_id: '', note: '' });
    setFile(null); setDocId('');
    axios.get(`${API}/mfg/equipment`, { params: { kind: 'machine', dept_code: 'ENG' } })
      .then((r) => {
        const rows = (r.data || []).filter((m) => !['retired'].includes(m.status));
        const isPrinter = (m) => /プリンタ|printer|ender|creality|phrozen|造形/i.test(`${m.name || ''} ${m.model || ''} ${m.maker || ''}`);
        const printFirst = /3d|３ｄ|プリント/i.test(fab?.make_method || '');
        setMachines(printFirst
          ? [...rows.filter(isPrinter), ...rows.filter((m) => !isPrinter(m))]
          : rows);
      })
      .catch(() => setMachines([]));
  }, [open, fab?.id, fab?.make_method]);

  // 材料使用量の初期値 = 部品マスターの g/個 × (OK + NG)
  useEffect(() => {
    if (!open || !perUnit) return;
    const total = ok + ng;
    setF((p) => ({ ...p, material_qty: total > 0 ? String(Math.round(perUnit * total * 1000) / 1000) : '' }));
  }, [ok, ng, open, perUnit]);

  const upload = async (sel) => {
    if (!sel) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', sel);
      fd.append('title', `${fab.fab_no} 試行写真`);
      fd.append('doc_type', 'photo');
      const r = await dmsApi.uploadLinked('eng_fabrication', fab.id, fd);
      setDocId(r?.document?.id || '');
      toast.success('写真を登録しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '写真の登録に失敗しました');
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (ok + ng <= 0) return toast.error('OK数とNG数の合計を1以上にしてください');
    if (ng > 0 && !f.ng_reason) return toast.error('NGがあるためNG理由を選んでください');
    if (needPhoto && !docId) {
      return toast.error(ng > 0 ? 'NGがあるため写真が必要です' : '初品の写真が必要です');
    }
    setBusy(true);
    try {
      const r = await axios.post(`${FAB}/${fab.id}/attempts`, {
        ok_qty: ok, ng_qty: ng,
        ng_reason: f.ng_reason || null, ng_note: f.ng_note.trim() || null,
        material_qty: Number(f.material_qty || 0),
        machine_id: f.machine_id || null,
        machine_name: machines.find((m) => m.id === f.machine_id)?.name || null,
        machine_code: machines.find((m) => m.id === f.machine_id)?.asset_no || null,
        document_id: docId || null,
        note: f.note.trim() || null,
      });
      (r.data?.warnings || []).forEach((w) => toast.warning(w));
      toast.success('記録しました');
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '記録できませんでした');
    } finally { setBusy(false); }
  };

  if (!fab) return null;
  const used = Number(f.material_qty || 0);
  const before = Number(fab.material_on_hand ?? NaN);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" aria-describedby={undefined}
        data-testid="fab-attempt-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            結果を記録 — {fab.fab_no} {fab.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">OK数</Label>
              <Input type="number" min="0" value={f.ok_qty}
                onChange={(e) => setF({ ...f, ok_qty: e.target.value })}
                data-testid="fab-attempt-ok" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">NG数</Label>
              <Input type="number" min="0" value={f.ng_qty}
                onChange={(e) => setF({ ...f, ng_qty: e.target.value })}
                data-testid="fab-attempt-ng" />
            </div>
          </div>

          {ng > 0 && (
            <div data-testid="fab-attempt-ng-block">
              <Label className="text-xs font-semibold text-zinc-700">NG理由（必須）</Label>
              <select value={f.ng_reason} onChange={(e) => setF({ ...f, ng_reason: e.target.value })}
                className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm"
                data-testid="fab-attempt-ng-reason">
                <option value="">選んでください</option>
                {NG_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <Input value={f.ng_note} onChange={(e) => setF({ ...f, ng_note: e.target.value })}
                placeholder="具体的な内容（任意）" className="mt-2"
                data-testid="fab-attempt-ng-note" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">
                材料使用量（{matUom}）
              </Label>
              <Input type="number" min="0" step="0.001" value={f.material_qty}
                onChange={(e) => setF({ ...f, material_qty: e.target.value })}
                data-testid="fab-attempt-material" />
              {perUnit > 0 && (
                <p className="mt-1 text-[11px] text-zinc-500">
                  初期値 = {perUnit}{matUom}/個 × {ok + ng}個
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">機械（任意）</Label>
              <select value={f.machine_id} onChange={(e) => setF({ ...f, machine_id: e.target.value })}
                className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm"
                data-testid="fab-attempt-machine">
                <option value="">選ばない</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id}>
                    {[m.asset_no, m.name].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              写真{needPhoto ? '（必須）' : '（任意）'}
              {needPhoto && (
                <span className="ml-1 font-normal text-rose-700"
                  data-testid="fab-attempt-photo-why">
                  — {ng > 0 ? 'NGがあるため写真が必要です' : '初品の写真が必要です'}
                </span>
              )}
            </Label>
            <Input type="file" accept="image/*,application/pdf" className="mt-1"
              onChange={(e) => { const s = e.target.files?.[0] || null; setFile(s); upload(s); }}
              data-testid="fab-attempt-photo" />
            <p className="mt-1 text-[11px] text-zinc-500" data-testid="fab-attempt-photo-hint">
              写真はドキュメントに保存され、この製作依頼と試行に紐付きます
            </p>
            {needPhoto && !docId && (
              <p className="mt-1 text-[11px] font-semibold text-rose-700"
                data-testid="fab-attempt-photo-required">
                {ng > 0 ? 'NGがあるため写真が必要です' : '初品の写真が必要です'}
              </p>
            )}
            {docId && (
              <p className="mt-1 text-[11px] text-emerald-700" data-testid="fab-attempt-photo-ok">
                {file?.name} を登録しました
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">メモ</Label>
            <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })}
              data-testid="fab-attempt-note" />
          </div>

          <div className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900"
            data-testid="fab-attempt-preview">
            材料 −{used}{matUom}
            {Number.isFinite(before) ? `（在庫 ${before} → ${Math.round((before - used) * 1000) / 1000}）` : ''}
            ／ 完成品 +{ok}{fab.uom || '個'} → {fab.deliver_to_label}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>閉じる</Button>
          <Button onClick={save} disabled={busy} data-testid="fab-attempt-save">
            {busy ? '保存中…' : '記録する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 中止（理由必須） */
export const FabCancelDialog = ({ fab, open, onOpenChange, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const submit = async () => {
    if (!reason.trim()) return toast.error('中止の理由を入力してください');
    setBusy(true);
    try {
      await axios.post(`${FAB}/${fab.id}/cancel`, { reason: reason.trim() });
      toast.success('中止しました');
      onOpenChange(false); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '中止できませんでした');
    } finally { setBusy(false); }
  };
  if (!fab) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white" aria-describedby={undefined}
        data-testid="fab-cancel-dialog">
        <DialogHeader><DialogTitle className="text-base">製作依頼を中止</DialogTitle></DialogHeader>
        <Label className="text-xs font-semibold text-zinc-700">理由（必須）</Label>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="例: 設計変更のため作り直し" data-testid="fab-cancel-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>やめる</Button>
          <Button variant="destructive" onClick={submit} disabled={busy}
            data-testid="fab-cancel-submit">中止する</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 詳細 — 集計と試行の記録 */
export const FabDetailDialog = ({ fab, open, onOpenChange, onChanged }) => {
  const [attemptOpen, setAttemptOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!fab) return null;

  const act = async (key) => {
    setBusy(true);
    try {
      const r = await axios.post(`${FAB}/${fab.id}/${key}`);
      (r.data?.warnings || []).forEach((w) => toast.warning(w));
      toast.success(key === 'start' ? '製作を開始しました' : '納品しました');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '変更できませんでした');
    } finally { setBusy(false); }
  };

  const undo = async (a) => {
    const reason = window.prompt('この記録を取り消す理由');
    if (!reason || !reason.trim()) return;
    try {
      await axios.post(`${FAB}/${fab.id}/attempts/${a.id}/undo`, { reason: reason.trim() });
      toast.success('記録を取り消しました');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '取り消せませんでした');
    }
  };

  const na = fab.next_action;
  const pct = fab.qty ? Math.min(100, Math.round((fab.ok_total / fab.qty) * 100)) : 0;
  const live = (fab.attempts || []);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl bg-white" aria-describedby={undefined}
          data-testid="fab-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {fab.fab_no} {fab.name}
              <span className="ml-2 text-xs font-normal text-zinc-500">{fab.status_label}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-5"
            data-testid="fab-detail-stats">
            <div><p className="text-[11px] text-zinc-500">依頼数</p>
              <p className="font-bold">{fab.qty}{fab.uom}</p></div>
            <div><p className="text-[11px] text-zinc-500">OK累計 / NG累計</p>
              <p className="font-bold" data-testid="fab-detail-ok-ng">
                {fab.ok_total} / {fab.ng_total}</p></div>
            <div><p className="text-[11px] text-zinc-500">試行回数</p>
              <p className="font-bold">{fab.attempt_count}回</p></div>
            <div><p className="text-[11px] text-zinc-500">材料使用量</p>
              <p className="font-bold">{fab.material_used_g}
                {(fab.material_lines || [])[0]?.uom || ''}</p></div>
            <div><p className="text-[11px] text-zinc-500">材料費（実績）</p>
              <p className="font-bold" data-testid="fab-detail-cost">
                {yen(fab.material_cost_actual)}</p></div>
          </div>

          <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200">
            <div className="h-full bg-teal-600" style={{ width: `${pct}%` }} />
          </div>

          <div className="flex items-center gap-2">
            {na && (
              <Button onClick={() => (na.key === 'attempt' ? setAttemptOpen(true) : act(na.key))}
                disabled={busy} className="bg-teal-700 hover:bg-teal-800"
                data-testid={`fab-primary-${na.key}`}>
                {na.label}
              </Button>
            )}
            {!['cancelled', 'delivered'].includes(fab.status) && (
              <Button variant="outline" onClick={() => setCancelOpen(true)}
                data-testid="fab-cancel-btn">中止</Button>
            )}
          </div>

          {fab.legacy_record ? (
            <p className="rounded-md border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm text-zinc-600"
              data-testid="fab-legacy-note">旧方式で記録</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-zinc-200">
              <table className="w-full text-sm" data-testid="fab-attempts-table">
                <thead className="bg-zinc-50 text-[11px] text-zinc-500">
                  <tr>
                    {['日時', 'OK', 'NG', 'NG理由', '材料', '材料費', '機械', '写真', '記録者', ''].map((h) => (
                      <th key={h} className="whitespace-nowrap px-2 py-1.5 text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {live.map((a) => (
                    <tr key={a.id} className={a.undone_at ? 'text-zinc-400 line-through' : ''}
                      data-testid={`fab-attempt-row-${a.no}`}>
                      <td className="whitespace-nowrap px-2 py-1.5">{(a.at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-2 py-1.5 tabular-nums">{a.ok_qty}</td>
                      <td className="px-2 py-1.5 tabular-nums">{a.ng_qty}</td>
                      <td className="px-2 py-1.5 text-xs">
                        {a.ng_reason_label || '—'}{a.ng_note ? `（${a.ng_note}）` : ''}
                      </td>
                      <td className="px-2 py-1.5 tabular-nums">
                        {a.material_qty}{a.material_uom || ''}
                      </td>
                      <td className="px-2 py-1.5 tabular-nums">{yen(a.material_cost)}</td>
                      <td className="px-2 py-1.5 text-xs" title={a.machine_name || ''}>
                        {a.machine_code || a.machine_name || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-xs">
                        {a.document_id ? (
                          <a href={`/documents?open=${a.document_id}`} target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-sky-700 hover:underline"
                            data-testid={`fab-attempt-photo-link-${a.no}`}>開く</a>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-xs">{a.by_name}</td>
                      <td className="px-2 py-1.5">
                        {!a.undone_at && (
                          <button onClick={() => undo(a)}
                            className="text-xs font-semibold text-rose-700 hover:underline"
                            data-testid={`fab-attempt-undo-${a.no}`}>この記録を取り消す</button>
                        )}
                        {a.undone_at && (
                          <span className="text-[11px]">取消（{a.undo_reason}）</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {live.length === 0 && (
                    <tr><td colSpan={10} className="px-2 py-6 text-center text-zinc-400"
                      data-testid="fab-attempts-empty">記録はまだありません</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <FabAttemptDialog fab={fab} open={attemptOpen} onOpenChange={setAttemptOpen}
        onSaved={() => onChanged?.()} />
      <FabCancelDialog fab={fab} open={cancelOpen} onOpenChange={setCancelOpen}
        onDone={() => onChanged?.()} />
    </>
  );
};
